import React from 'react';
import speakTextChangelog from './speak-text.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { NodeConfig, NodeField, NodeFieldType, NodeInputType, NodeOutput, NodeType, SpeakTextNodeState } from '../..';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

export const config: NodeConfig = {
  version: '1.3',
  title: 'Speak Text',
  identifier: 'SpeakText',
  command_id: 'call.speak',
  icon: 'telnyx',
  background: '#000000',
  changelog: speakTextChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'call.speak.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'call.speak.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.speak.started',
      label: 'call.speak.started',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.speak.started.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.speak.ended',
      label: 'call.speak.ended',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.speak.ended.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [
    {
      webhook: 'call.speak.started',
      expected: true,
      multi: false,
    },
    {
      webhook: 'call.speak.ended',
      expected: true,
      multi: false,
    },
  ],
};

export const state: SpeakTextNodeState = {
  call_control_id: { type: NodeInputType.Expression, value: '' },
  payload: { type: NodeInputType.Expression, value: '' },
  payload_type: { type: NodeInputType.Option, value: 'text' },
  service_level: { type: NodeInputType.Option, value: 'premium' },
  stop: { type: NodeInputType.Option, value: '' },
  voice: { type: NodeInputType.Option, value: 'Telnyx.KokoroTTS' },
  language: { type: NodeInputType.Option, value: 'en-US' },
  elevenlabs_api_key: '',
  elevenlabs_voice_id: '',
  elevenlabs_model_id: 'eleven_turbo_v2',
  aws_polly_voice_id: 'Joanna',
  aws_polly_neural: 'standard',
  kokoro_voice_id: 'af_bella',
  azure_voice_id: 'en-US-AvaMultilingualNeural',
  client_state: { type: NodeInputType.Expression, value: '' },
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: false,
  },
  {
    id: 'payload',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Payload',
    description: 'The text or SSML to be converted into speech. There is a 3,000 character limit.',
    placeholder: 'The text or SSML to be converted into speech.',
    required: true,
  },
  // {
  //   id: 'payload_type',
  //   tab: '1',
  //   type: NodeFieldType.OptionExpression,
  //   default_type: NodeInputType.Option,
  //   label: 'Payload Type',
  //   description:
  //     'The type of the provided payload. The payload can either be plain text, or Speech Synthesis Markup Language (SSML).',
  //   required: false,
  //   options: [
  //     { label: 'Text', value: 'text' },
  //     { label: 'Speech Synthesis Markup Language', value: 'ssml' },
  //   ],
  // },
  // {
  //   id: 'service_level',
  //   tab: '1',
  //   type: NodeFieldType.OptionExpression,
  //   default_type: NodeInputType.Option,
  //   label: 'Service Level',
  //   description:
  //     'This parameter impacts speech quality, language options and payload types. When using basic, only the en-US language and payload type text are allowed.',
  //   required: false,
  //   options: [
  //     { label: 'Basic Speech', value: 'basic' },
  //     { label: 'Premium Speech', value: 'premium' },
  //   ],
  // },
  {
    id: 'stop',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Stop Audio',
    description:
      'When specified, it stops the current audio being played. Specify current to stop the current audio being played, and to play the next file in the queue. Specify all to stop the current audio file being played and to also clear all audio files from the queue.',
    required: false,
    placeholder: 'Specify to stop audio before speaking',
    options: [
      { label: 'None', value: '' },
      { label: 'Current', value: 'current' },
      { label: 'All', value: 'all' },
    ],
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
  {
    id: 'voice',
    tab: '2',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Voice',
    description:
      'The voice to be used by the voice assistant. Currently we support ElevenLabs and AWS voices. Using Male or Female voices with the language field translates to AWS Polly voices for the specified language.',
    required: false,
    options: [
      { label: 'Telnyx.KokoroTTS', value: 'Telnyx.KokoroTTS' },
      { label: 'ElevenLabs', value: 'ElevenLabs' },
      { label: 'AWS.Polly', value: 'AWS.Polly' },
      { label: 'Azure', value: 'Azure' },
      { label: 'Male', value: 'male' },
      { label: 'Female', value: 'female' },
    ],
  },
  {
    id: 'language',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Language',
    description: 'The language you want spoken. This parameter is ignored when a Polly.* voice is specified.',
    required: false,
    options: [
      { label: 'arb', value: 'arb' },
      { label: 'cmn-CN', value: 'cmn-CN' },
      { label: 'cy-GB', value: 'cy-GB' },
      { label: 'da-DK', value: 'da-DK' },
      { label: 'de-DE', value: 'de-DE' },
      { label: 'en-AU', value: 'en-AU' },
      { label: 'en-GB', value: 'en-GB' },
      { label: 'en-GB-WLS', value: 'en-GB-WLS' },
      { label: 'en-IN', value: 'en-IN' },
      { label: 'en-US', value: 'en-US' },
      { label: 'es-ES', value: 'es-ES' },
      { label: 'es-MX', value: 'es-MX' },
      { label: 'es-US', value: 'es-US' },
      { label: 'fr-CA', value: 'fr-CA' },
      { label: 'fr-FR', value: 'fr-FR' },
      { label: 'hi-IN', value: 'hi-IN' },
      { label: 'is-IS', value: 'is-IS' },
      { label: 'it-IT', value: 'it-IT' },
      { label: 'ja-JP', value: 'ja-JP' },
      { label: 'ko-KR', value: 'ko-KR' },
      { label: 'nb-NO', value: 'nb-NO' },
      { label: 'nl-NL', value: 'nl-NL' },
      { label: 'pl-PL', value: 'pl-PL' },
      { label: 'pt-BR', value: 'pt-BR' },
      { label: 'pt-PT', value: 'pt-PT' },
      { label: 'ro-RO', value: 'ro-RO' },
      { label: 'ru-RU', value: 'ru-RU' },
      { label: 'sv-SE', value: 'sv-SE' },
      { label: 'tr-T', value: 'tr-T' },
    ],
  },
  {
    id: 'elevenlabs_api_key',
    tab: 'elevenlabs_voice',
    type: NodeFieldType.Secret,
    label: 'Eleven Labs API Key',
    description:
      'The secret that contains your ElevenLabs API key. If you have not created a secret yet, you can do so from the workspace tab named Secrets.',
    required: false,
  },
  {
    id: 'elevenlabs_voice_id',
    tab: 'elevenlabs_voice',
    type: NodeFieldType.Option,
    default_type: NodeInputType.ElevenLabsVoice,
    label: 'ElevenLabs Voice',
    description:
      'ElevenLabs voices, identified by voice IDs, determine the underlying speech synthesis technology used to convert text into speech.',
    required: false,
    placeholder: 'The voice model to use to convert text-to-speech.',
  },
  {
    id: 'elevenlabs_model_id',
    type: NodeFieldType.Option,
    default_type: NodeInputType.ElevenLabsVoice,
    label: 'ElevenLabs Voice',
    description:
      'ElevenLabs models, identified by model IDs, determine the underlying capabilites of the voice model such as the languages they can speak in.',
    required: false,
    placeholder: 'The model to use to convert text-to-speech.',
  },
  {
    id: 'aws_polly_voice_id',
    tab: 'aws_voice',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Option,
    label: 'AWS Polly Voice',
    description: 'An AWS.Polly voice.',
    required: false,
    placeholder: 'The AWS.Polly voice to use to convert text-to-speech.',
  },
  {
    id: 'aws_polly_neural',
    tab: 'aws_polly_settings',
    type: NodeFieldType.Toggle,
    label: 'AWS Polly Voice Type',
    description:
      'Only certain voices support different voice types. Enable the neural setting to produce higher quality voices than the standard voices. Enable generative voices for the most human-like, emotionally engaged, and adaptive conversational voices available. Enable the long form setting to produce human-like, highly expressive, and emotionally adept voices.',
    required: false,
    options: [
      { label: 'Standard', value: 'standard' },
      { label: 'Neural', value: 'neural' },
    ],
  },
  {
    id: 'kokoro_voice_id',
    tab: 'kokoro_voice',
    type: NodeFieldType.Option,
    label: 'Kokoro Voice Type',
    description: 'The Kokoro voice used to convert text-to-speech.',
    required: false,
    placeholder: 'The voice model to use to convert text-to-speech.',
  },
  {
    id: 'azure_voice_id',
    tab: 'azure_voice',
    type: NodeFieldType.Option,
    label: 'Azure Voice Type',
    description: 'The Azure voice used to convert text-to-speech.',
    required: false,
    placeholder: 'The voice model to use to convert text-to-speech.',
  },
];

export const output: NodeOutput = {
  default: {
    'call.speak.started': {
      call: {
        speak: {
          started: {
            call_control_id: 'string',
            call_leg_id: 'string',
            call_session_id: 'string',
            client_state: 'string | null',
            connection_id: 'number',
            state: 'string',
          },
        },
      },
    },
    'call.speak.ended': {
      call: {
        speak: {
          ended: {
            call_control_id: 'string',
            call_leg_id: 'string',
            call_session_id: 'string',
            client_state: 'string | null',
            connection_id: 'number',
            state: 'string',
          },
        },
      },
    },
  },
  dynamic: null,
};

export const SpeakText: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.speak') return null;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
